import { AddLocationAlt as AddLocationAltIcon } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  AreaAdditionFormFields,
  ICustomField,
  isInnerItemEditingAtom,
  Log,
} from "@ryde/shared";

import { NumberTemplate } from "../number.template";
import { SwitchTemplate } from "../switch.template";
import { AreaAdditionItem } from "./area-addition.item";
import { AreaAdditionItemAdd } from "./area-addition.item.add";
import { emptyAreaAddition } from "./area-addition.models";
import { AreaAdditionsTemplateWrapperStyled } from "./area-addition.styled";
import {
  buildSortedAreaAdditionEntries,
  normalizeAreaAdditionFieldData,
} from "./area-addition.utils";

export const AreaAdditions: FC<ICustomField> = ({ name, formAttributes }) => {
  const defaultPriceFieldName = `${name}.${AreaAdditionFormFields.defaultPrice}`;
  const priceListFieldName = `${name}.${AreaAdditionFormFields.prices}`;
  const skipCountSecondaryAreasFieldName = `${name}.${AreaAdditionFormFields.skipCountSecondaryAreas}`;
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext();
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);

  const pricingListWatched = useWatch({ control, name: priceListFieldName });

  const rawPricingList = useMemo(() => {
    const availabilityListValue = normalizeAreaAdditionFieldData(
      pricingListWatched,
      name,
      setValue,
    );
    Log.debug("Area addition list normalized value", availabilityListValue);
    return availabilityListValue;
  }, [name, setValue, pricingListWatched]);

  const sortedPricingEntries = useMemo(
    () => buildSortedAreaAdditionEntries(rawPricingList),
    [rawPricingList],
  );

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  const removeHandler = useEvent((formIndex: number) => {
    Log.debug("Remove area addition handler fired for form index ", formIndex);

    const rawList = normalizeAreaAdditionFieldData(
      getValues(priceListFieldName),
      name,
      setValue,
    );

    if (!rawList?.at(formIndex)) {
      Log.warnAndToast(
        t("errors.ITEM_DONT_EXISTS_IN_LIST"),
        formIndex,
        rawList,
      );
      return;
    }

    const pricingListUpdated = rawList.toSpliced(formIndex, 1);
    setValue(priceListFieldName, pricingListUpdated || [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  return (
    <AreaAdditionsTemplateWrapperStyled>
      <Chip
        label={t("general.ADDITIONS_PER_AREA")}
        icon={<AddLocationAltIcon />}
      />
      <NumberTemplate
        name={defaultPriceFieldName}
        formAttributes={formAttributes}
      />
      <SwitchTemplate name={skipCountSecondaryAreasFieldName} />

      <div
        className="area-additions__container"
        data-inner-editing={editingCount > 0 ? "true" : undefined}
      >
        {!!sortedPricingEntries?.length &&
          sortedPricingEntries.map(({ item: priceItem, formIndex }) => {
            const uniqueKeyValue = `${formIndex}_${priceItem.startingFromAreaIndex ?? "_"}_${priceItem.areaIds?.join("-") || "_"}_${priceItem?.price || 0}`;
            return (
              <AreaAdditionItem
                key={uniqueKeyValue}
                fieldName={priceListFieldName}
                control={control}
                index={formIndex}
                setValue={setValue}
                getValues={getValues}
                value={priceItem || emptyAreaAddition}
                removeHandler={removeHandler}
                onEditStart={handleEditStart}
                onEditEnd={handleEditEnd}
              />
            );
          })}
        <AreaAdditionItemAdd
          fieldName={priceListFieldName}
          index={rawPricingList?.length || 0}
          setValue={setValue}
          getValues={getValues}
          onEditStart={handleEditStart}
          onEditEnd={handleEditEnd}
        />
      </div>
    </AreaAdditionsTemplateWrapperStyled>
  );
};
